// System log (spec §3.15, Part C): every change people made in the app, newest first — when, who,
// the line (with its source, A2000 or Deposco), what changed in plain words — with Go to line and
// Restore. "By people" only: no A2000
// or Deposco rows yet (docs/v7-next-steps.md). It reads `ChangeView`s only (`/api/routing/changes`,
// `changes.ts`): from `change-entry` today, from `audit_log` once the platform allows it (P13).
//
// Restore saves the old value through CRUD, so it is a change of its own ("Restored …").

import { useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ArrowRightIcon, RotateCcwIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  DEPOSCO_CORP,
  CUSTOMER_ASSIGNMENTS,
  DIVISION_ABBREVS,
  CUSTOMER_NAMES,
  ROUTING_RULES,
  NO_HOLIDAYS,
  PAGES,
  PRE_ROUTES,
  changeText,
  checkAppointment,
  checkDate,
  checkInOut,
  countText,
  fieldLabel,
  formatDay,
  inSource,
  isFlag,
  isGroupedLine,
  localToday,
  parseTime,
  parseWhole,
  restorePlan,
  restoreText,
  routingRows,
  seesAdmin,
  isMyChange,
  customerSearchText,
  customerKey,
  ROUTING_API,
  shownCustomerText,
  ACTION_WORDS,
  logViewKey,
  pushStep,
  undoableStep,
  clockText,
} from "@app/shared";
import type {
  ChangeAction,
  ChangeView,
  DateCell,
  LineSource,
  LineView,
  LogFacts,
  LogView,
  ViewStep,
} from "@app/shared";
import {
  Button,
  DataError,
  RegisteredPage,
  TableSkeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  apiGet,
  useMe,
} from "@axny/platform-ui";
import type { CustomerNamesPayload } from "@app/shared";
import { CUSTOMER_NAMES_KEY, useRulesFirst } from "./customer-names-tab";
import { useTableColumns } from "./column-drag";
import { PRE_ROUTES_KEY, linesKey, useHolidays, useLinesData, usePreRoutes } from "./data";
import { changedAt, useChanges } from "./line-history";
import { MessageProvider, notify, useMessage } from "./message";
import { DivisionText } from "./named";
import { useShipFlow } from "./ship-flow";
import type { PickupField, RowField } from "./shipping-writer";
import { DayRangePicker, shipRange } from "./shipping-page";
import type { ShipRange } from "./shipping-page";
import { SourceBadge, SourcePicker, useSourceFilter } from "./source-filter";
import { ColumnsMenu, SearchBox } from "./toolbar";
import { MyCustomersChip, useMyCustomers } from "./my-customers";
import { useWriter } from "./writer";
import type { PageSearch } from "../../pages";
import { AskAiBar } from "./ask-ai-bar";
import { useSystemLogAskAi } from "./axai-system-log";

/** The System log's columns its Columns menu orders (Go to line and Restore are always last). */
const LOG_COLUMNS = [
  { id: "when", header: "When", className: "w-24" },
  { id: "who", header: "Who", className: "w-40" },
  { id: "line", header: "Line", className: "w-72" },
  { id: "change", header: "Change" },
];

/** `line`: a line's details opened it (Open in System log) — that line's changes, every date. */
export function SystemLogPage({ line }: { line?: string } = {}) {
  return (
    <RegisteredPage id="system-log">
      <MessageProvider>
        <SystemLogScreen line={line} />
      </MessageProvider>
    </RegisteredPage>
  );
}

/** "4:12 PM" and, under it, "Today" or "10/12/26"; an entry written before its time was kept has its day only. */
function When({ change, today }: { change: ChangeView; today: string }) {
  const d = new Date(change.at);
  const day = change.timeKnown ? localToday(d) : change.at.slice(0, 10);
  return (
    <div className="leading-tight">
      {change.timeKnown ? (
        <p className="font-semibold tabular-nums">{clockText(d)}</p>
      ) : (
        // Written before 10/22 (migration 009): the platform kept its day only.
        <Tooltip>
          <TooltipTrigger render={<p className="text-muted-foreground cursor-help font-semibold" />}>—</TooltipTrigger>
          <TooltipContent>Saved before the System log kept the time of day: its date only</TooltipContent>
        </Tooltip>
      )}
      <p className="text-muted-foreground text-xs">{day === today ? "Today" : formatDay(day)}</p>
    </div>
  );
}

/** An entry as Ask AI reads it: its day, and what its line says now (else only its own line text). */
function factsOf(c: ChangeView, line: LineView | undefined): LogFacts {
  return {
    change: c,
    day: c.timeKnown ? localToday(new Date(c.at)) : c.at.slice(0, 10),
    customers: line ? [shownCustomerText(line), ...line.customerNames, ...line.customers.map((x) => x.code)] : [],
    pickTickets: line ? line.pts.map((x) => x.pickTicket) : [],
    line: c.lineText,
  };
}

const DATE_FIELDS: readonly string[] = ["readyDate", "schedShip", "routedOn", "appointmentDate"];

function SystemLogScreen({ line }: { line?: string }) {
  const today = useMemo(() => localToday(), []);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const message = useMessage();
  // A shown name restored changes routing windows as a rename does: the same check first.
  const rulesFirst = useRulesFirst();
  const me = useMe();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";
  const admin = seesAdmin(me.data?.authenticated ? me.data.features : undefined);
  const [range, setRange] = useState<ShipRange>(() => shipRange("7d", today));
  const [search, setSearch] = useState("");
  // A person and an action: set by Ask AI only (`apply_view`), each a chip with its ×.
  const [person, setPerson] = useState("");
  const [action, setAction] = useState<ChangeAction | "">("");
  const [source, setSource] = useSourceFilter();
  // My customers (`my-customers.tsx`): off by default here — people look at every change.
  const my = useMyCustomers("system-log");
  const tableColumns = useTableColumns("system-log", LOG_COLUMNS);
  // One line's changes (Open in System log), every date, until its chip's × takes it off.
  const [onLine, setOnLine] = useState(line ?? "");
  const changes = useChanges(onLine ? { line: onLine } : { from: range.from, to: range.to });
  // The lines and pickups these changes name, as they are now: who they are, open or finished,
  // and the values Restore compares — only those, never the whole Worklist.
  const ids = useMemo(() => {
    const all = changes.data?.changes ?? [];
    return {
      lineIds: [
        ...new Set(all.map((c) => c.lineId || (c.entity === "routing-line" ? c.recordId : "")).filter(Boolean)),
      ],
      pickupIds: [...new Set(all.filter((c) => c.entity === "pickup").map((c) => c.recordId))],
    };
  }, [changes.data]);
  const data = useLinesData(ids, !!changes.data);
  // A pre-route's entries: Restore compares with it as it is now (the shell's read, shared).
  const preRoutes = usePreRoutes();
  const preRouteOf = (id: string) => preRoutes.data?.preRoutes.find((p) => p.id === id);
  const holidays = useHolidays();
  const writer = useWriter(linesKey(ids), who, today);
  const payload = data.data;
  const calendar = holidays.data?.calendar ?? NO_HOLIDAYS;
  const lines = useMemo(
    () => (payload ? routingRows(payload, today, calendar).assembled.lines : []),
    [payload, today, calendar],
  );
  const flow = useShipFlow({ writer, payload, lines, today });
  const lineOf = useMemo(() => new Map(lines.flatMap((l) => (l.stored ? [[l.stored.id, l] as const] : []))), [lines]);
  // Ask AI (`axai-system-log.ts`): the log's own filters it may set, and its steps for Undo.
  const logView: LogView = { preset: range.preset, from: range.from, to: range.to, search, person, action };
  const [aiSteps, setAiSteps] = useState<ViewStep<LogView>[]>([]);
  const turnBefore = useRef<LogView | null>(null);
  const aiStep = undoableStep(aiSteps, logViewKey(logView));
  const setLogView = (v: LogView) => {
    // Other days asked for: the log leaves one line's changes (Open in System log) to show them.
    if (v.preset !== range.preset || v.from !== range.from || v.to !== range.to) setOnLine("");
    setRange({ preset: v.preset, from: v.from, to: v.to });
    setSearch(v.search);
    setPerson(v.person);
    setAction(v.action);
  };
  // What the page reads, as Ask AI counts it: set below once the entries are read.
  const asked = useRef<{ range: LogFacts[]; shown: LogFacts[] }>({ range: [], shown: [] });
  useSystemLogAskAi({
    today,
    view: logView,
    apply: (next, unapplied) => {
      const question = turnBefore.current;
      setAiSteps((steps) =>
        pushStep(steps, { before: question ?? logView, version: logViewKey(next), unapplied }, question),
      );
      setLogView(next);
    },
    rangeFacts: () => asked.current.range,
    shownFacts: () => asked.current.shown,
    factsOf: (cs) => cs.map((c) => factsOf(c, undefined)),
    words: { when: changedAt, change: changeText },
  });

  if (changes.isPending || data.isPending) return <TableSkeleton />;
  if (changes.isError) return <DataError error={changes.error} />;
  if (data.isError) return <DataError error={data.error} />;

  const lineFor = (c: ChangeView) => lineOf.get(c.lineId || (c.entity === "routing-line" ? c.recordId : ""));
  /**
   * Where an entry's record comes from: its line's source, a pre-route's, a pickup's lines', a
   * division's (corp DEPOSCO is Deposco's); none for a list value.
   */
  const sourcesOf = (c: ChangeView): LineSource[] => {
    const line = lineFor(c);
    if (line) return [line.source];
    if (c.entity === "pre-route") {
      const pre = preRouteOf(c.recordId);
      return pre ? [pre.source] : [];
    }
    if (c.entity === DIVISION_ABBREVS.id)
      return [c.lineText.startsWith(`Division ${DEPOSCO_CORP}`) ? "Deposco" : "A2000"];
    if (c.entity === CUSTOMER_NAMES.id) return [c.lineText.startsWith("Customer Deposco") ? "Deposco" : "A2000"];
    if (c.entity === ROUTING_RULES.id) return [c.lineText.startsWith("Routing rule Deposco") ? "Deposco" : "A2000"];
    // A label may cover customers of either source.
    if (c.entity === CUSTOMER_ASSIGNMENTS.id) return ["A2000", "Deposco"];
    if (c.entity === "pickup")
      return [...new Set(flow.rows.filter((r) => r.pickup.id === c.recordId).map((r) => r.line.source))].sort();
    return [];
  };
  const q = search.trim().toLowerCase();
  const all = changes.data.changes;
  const inThisSource = (c: ChangeView) => source === "all" || sourcesOf(c).some((x) => inSource(x, source));
  // My customers on: an entry of a line of theirs; one with no line (a list value, a pickup) stays.
  const ofMine = my.mine ? all.filter((c) => isMyChange(lineFor(c), my.filter)) : all;
  const shown = ofMine.filter((c) => {
    if (!inThisSource(c)) return false;
    if (person && c.personName !== person) return false;
    if (action && c.action !== action) return false;
    if (!q) return true;
    const l = lineFor(c);
    // A pickup's own entries: found by its PICK UP # and its id too.
    const pickup = c.entity === "pickup" ? payload?.pickups.find((p) => p.id === c.recordId) : undefined;
    return [
      c.lineText,
      c.personName,
      changeText(c),
      ...c.changes.map((x) => fieldLabel(c.entity, x.f)),
      l ? customerSearchText(l) : "",
      l?.divisions.join(" ") ?? "",
      l?.divs.map((d) => d.abbrev).join(" ") ?? "",
      l?.pos.join(" ") ?? "",
      l?.pts.map((p) => p.pickTicket).join(" ") ?? "",
      l?.wmsNo ?? "",
      c.entity === "pickup" ? c.recordId : "",
      pickup?.loadRef ?? "",
    ]
      .join(" ")
      .toLowerCase()
      .includes(q);
  });
  asked.current = {
    range: ofMine.filter(inThisSource).map((c) => factsOf(c, lineFor(c))),
    shown: shown.map((c) => factsOf(c, lineFor(c))),
  };

  /** The record a change is about, as it is now; null when it is not there any more. */
  const currentOf = (c: ChangeView): Record<string, unknown> | null => {
    if (c.entity === "routing-line") return lineOf.get(c.recordId)?.fields ?? null;
    if (c.entity === "pickup")
      return (payload?.pickups.find((p) => p.id === c.recordId) as Record<string, unknown> | undefined) ?? null;
    if (c.entity === "shipping-row")
      return (payload?.shippingRows.find((r) => r.id === c.recordId) as Record<string, unknown> | undefined) ?? null;
    if (c.entity === "pre-route") return (preRouteOf(c.recordId) as Record<string, unknown> | undefined) ?? null;
    if (c.entity === DIVISION_ABBREVS.id)
      return (payload?.divisionAbbrevs.find((d) => d.id === c.recordId) as Record<string, unknown> | undefined) ?? null;
    if (c.entity === CUSTOMER_NAMES.id)
      return (payload?.customerNames.find((d) => d.id === c.recordId) as Record<string, unknown> | undefined) ?? null;
    if (c.entity === ROUTING_RULES.id) {
      const r = payload?.routingRules.find((d) => d.id === c.recordId);
      // As the entry says them: a day count as text, "" for none.
      const flag = (v: unknown) => (isFlag(v) ? "1" : "0");
      return r
        ? {
            ...r,
            openBdays: r.openBdays ?? "",
            dueBdays: r.dueBdays ?? "",
            noRule: flag(r.noRule),
            separate: flag(r.separate),
          }
        : null;
    }
    return null;
  };
  /**
   * One rule for Restore: nothing on a line or pickup that is shipped or cancelled, nor on a row
   * taken off its pickup. A pickup's own fields (Date, Carrier, PICK UP #…) restore while it is
   * Scheduled: for every line on it, as an edit there would.
   */
  const closedOf = (c: ChangeView): string | null => {
    const line = lineFor(c);
    if (line?.finished) return `Line ${line.finished.status.toLowerCase()} — can't restore`;
    if (c.entity === "pickup") {
      const pickup = payload?.pickups.find((x) => x.id === c.recordId);
      if (pickup && pickup.status !== "Scheduled") return `Pickup ${pickup.status.toLowerCase()} — can't restore`;
    }
    if (c.entity === "pre-route") {
      const pre = preRouteOf(c.recordId);
      if (pre && pre.status !== "Waiting") return `Pre-route ${pre.status.toLowerCase()} — can't restore`;
    }
    // An abbreviation is the Admin page's: the server refuses anyone else's write.
    if ((c.entity === DIVISION_ABBREVS.id || c.entity === CUSTOMER_NAMES.id || c.entity === ROUTING_RULES.id) && !admin)
      return "Managers only — a manager restores it (the Admin page)";
    if (c.entity === "shipping-row") {
      const row = payload?.shippingRows.find((x) => x.id === c.recordId);
      if (row?.status === "Shipped") return "Line shipped — can't restore";
      if (row?.status === "Removed") return "Taken off its pickup — can't restore";
    }
    return null;
  };
  const planOf = (c: ChangeView) => {
    const line = lineFor(c);
    return restorePlan(c, currentOf(c), {
      closed: closedOf(c),
      legacy: !!line?.stored && !isGroupedLine(line.stored),
    });
  };

  // The pages are code routes (`pages.tsx`): their paths and `PageSearch` are not in the router's types.
  const go = (to: string, search: PageSearch) => void navigate({ to: to as never, search: search as never });
  const goTo = (c: ChangeView) => {
    // On the Shipping schedule, with the line's row (or the pickup's rows) selected.
    if (c.entity === "pickup") return go(PAGES.shipping.path, { tab: "schedule", pickup: c.recordId });
    if (c.entity === "shipping-row") return go(PAGES.shipping.path, { tab: "schedule", line: c.lineId });
    if (c.entity === "shipment") return go(PAGES.shipping.path, { tab: "log", shipment: c.recordId });
    if (c.entity === "pre-route") return go(PAGES["pre-route"].path, { tab: "all" });
    if (c.entity === ROUTING_RULES.id) return go(PAGES.admin.path, { admin: "routing-rules" });
    if (c.entity === CUSTOMER_NAMES.id) return go(PAGES.admin.path, { admin: "customers" });
    if (c.entity === DIVISION_ABBREVS.id || c.entity === CUSTOMER_ASSIGNMENTS.id)
      return go(PAGES.admin.path, { admin: c.entity === DIVISION_ABBREVS.id ? "divisions" : "assignments" });
    const line = lineFor(c);
    if (!line)
      return void message.error("Line not found", "That line is no longer on the Worklist, Shipping or History.");
    // An open line opens on the Worklist; a finished one on History.
    return go(line.finished ? PAGES.history.path : PAGES.worklist.path, { line: line.stored!.id });
  };

  const restore = async (c: ChangeView) => {
    const plan = planOf(c);
    if (!plan.ok) return message.error("Can't restore this change", plan.why);
    // A pickup's field is every line's on it: the confirm says how many.
    const onPickup = c.entity === "pickup" ? flow.rows.filter((r) => r.pickup.id === c.recordId).length : 0;
    const every = onPickup ? ` It changes the pickup for every line on it · ${countText(onPickup, "line")}.` : "";
    const ok = await message.confirm(
      plan.changedSince ? "Changed again since — are you sure?" : "Are you sure?",
      plan.changedSince
        ? `Someone changed it again after this change. Restore puts back the value from before this change, over what it is now.${every}`
        : `Restore ${c.personName}'s change of ${changedAt(c)}.${every}`,
      { lines: plan.fields.map(restoreText), confirmLabel: "Restore" },
    );
    if (!ok) return;
    const note = `Restored the value from before ${c.personName}'s change of ${changedAt(c)}`;
    if (c.entity === "routing-line") {
      const line = lineOf.get(c.recordId)!;
      for (const x of plan.fields) {
        // The same checks as typing it: a date restored may be in the past now.
        if (DATE_FIELDS.includes(x.f) && x.restoreTo) {
          const problems = checkDate(x.f as DateCell, x.restoreTo, {
            today,
            ptDate: line.ptDate,
            start: line.start,
            cancel: line.cancel,
            readyDate: line.fields.readyDate,
            schedShip: line.fields.schedShip,
            onPickup: !!line.onPickup,
          });
          const error = problems.find((p) => p.level === "error");
          if (error) return message.error(`Can't restore ${x.label}`, error.message);
          if (
            problems.length &&
            !(await message.confirm("Are you sure?", `Restore ${x.label}? Check these first:`, {
              lines: problems.map((p) => p.message),
              confirmLabel: "Restore anyway",
            }))
          )
            return;
        }
      }
      // One save for the whole change: a status and the Routed on it set come back together.
      if (!(await writer.restoreFields(line, Object.fromEntries(plan.fields.map((x) => [x.f, x.restoreTo])), note)))
        return;
    } else if (c.entity === "pre-route") {
      const pre = preRouteOf(c.recordId)!;
      const change: Record<string, unknown> = {};
      for (const x of plan.fields) {
        if (x.f === "appointmentDate") {
          const problem = checkAppointment(x.restoreTo, { today, cancel: pre.cancelDate });
          if (problem) return message.error("Can't restore Pickup appt", problem);
        }
        change[x.f] = x.restoreTo || (x.f === "appointmentDate" ? null : "");
      }
      try {
        await writer.kit.put(
          PRE_ROUTES,
          pre.id,
          change,
          {
            action: "restore",
            lineText: c.lineText,
            changes: plan.fields.map((x) => ({ f: x.f, from: x.now, to: x.restoreTo })),
            note,
          },
          { field: plan.fields[0]!.f, value: plan.fields[0]!.now },
        );
      } catch (e) {
        return message.error("Couldn't restore", e instanceof Error ? e.message : String(e));
      } finally {
        await queryClient.invalidateQueries({ queryKey: PRE_ROUTES_KEY });
      }
    } else if (c.entity === CUSTOMER_NAMES.id) {
      const x = plan.fields[0]!;
      const record = payload?.customerNames.find((d) => d.id === c.recordId);
      if (record) {
        const customers = await queryClient.fetchQuery({
          queryKey: CUSTOMER_NAMES_KEY,
          queryFn: ({ signal }) => apiGet<CustomerNamesPayload>(`${ROUTING_API}/admin/customers`, signal),
          staleTime: 10_000,
        });
        const key = customerKey(record.source === "Deposco" ? "Deposco" : "A2000", record.code);
        const first = await rulesFirst.run(customers, [{ key, shownName: x.restoreTo }]);
        if (!first) return;
        if (first.failed.length)
          await message.error("Some routing rules weren't saved", "The shown name is restored next.", first.failed);
      }
      try {
        await writer.kit.put(
          CUSTOMER_NAMES,
          c.recordId,
          { shownName: x.restoreTo, updatedBy: who },
          {
            action: "restore",
            lineText: c.lineText,
            changes: [{ f: x.f, from: x.now, to: x.restoreTo }],
            note: x.restoreTo ? note : `${note} — back to the default`,
          },
          { field: x.f, value: x.now },
        );
      } catch (e) {
        return message.error("Couldn't restore", e instanceof Error ? e.message : String(e));
      } finally {
        await queryClient.invalidateQueries({ queryKey: ["routing"] });
      }
    } else if (c.entity === ROUTING_RULES.id) {
      // A day count back as a number ("" is none), a flag as 1 or 0.
      const value = (f: string, v: string) =>
        f === "notes" ? v : f === "noRule" || f === "separate" ? (v === "1" ? 1 : 0) : v === "" ? null : Number(v);
      try {
        await writer.kit.put(
          ROUTING_RULES,
          c.recordId,
          { ...Object.fromEntries(plan.fields.map((x) => [x.f, value(x.f, x.restoreTo)])), updatedBy: who },
          {
            action: "restore",
            lineText: c.lineText,
            changes: plan.fields.map((x) => ({ f: x.f, from: x.now, to: x.restoreTo })),
            note,
          },
          { field: plan.fields[0]!.f, value: plan.fields[0]!.now },
        );
      } catch (e) {
        return message.error("Couldn't restore", e instanceof Error ? e.message : String(e));
      } finally {
        await queryClient.invalidateQueries({ queryKey: ["routing"] });
      }
    } else if (c.entity === DIVISION_ABBREVS.id) {
      const x = plan.fields[0]!;
      try {
        await writer.kit.put(
          DIVISION_ABBREVS,
          c.recordId,
          { abbreviation: x.restoreTo, updatedBy: who },
          {
            action: "restore",
            lineText: c.lineText,
            changes: [{ f: x.f, from: x.now, to: x.restoreTo }],
            note: x.restoreTo ? note : `${note} — back to the default`,
          },
          { field: x.f, value: x.now },
        );
      } catch (e) {
        return message.error("Couldn't restore", e instanceof Error ? e.message : String(e));
      } finally {
        await queryClient.invalidateQueries({ queryKey: ["routing"] });
      }
    } else if (c.entity === "pickup") {
      const pickup = payload!.pickups.find((p) => p.id === c.recordId)!;
      const rows = flow.rows.filter((r) => r.pickup.id === pickup.id);
      for (const x of plan.fields) {
        if (x.f === "pickupDate" && x.restoreTo < today)
          return message.error("Can't restore Date", "The pickup date can't be in the past");
        if (!(await flow.shipping.editPickup(pickup, x.f as PickupField, x.restoreTo, rows, "restore"))) return;
      }
    } else {
      const row = flow.rows.find((r) => r.row.id === c.recordId);
      if (!row) return message.error("Can't restore this change", "That line is no longer on the Shipping schedule.");
      for (const x of plan.fields) {
        let value: string | number | null = x.restoreTo;
        if (x.f === "ctns" || x.f === "plts") {
          const n = parseWhole(x.restoreTo, x.label);
          value = n.ok ? n.value : null;
        }
        if (x.f === "inTime" || x.f === "outTime") {
          const t = parseTime(x.restoreTo);
          const problem = t.ok
            ? x.f === "inTime"
              ? checkInOut(t.value, row.row.outTime)
              : checkInOut(row.row.inTime, t.value)
            : t.message;
          if (problem) return message.error(`Can't restore ${x.label}`, problem);
        }
        await flow.shipping.editRow(row, x.f as RowField, value, "restore");
      }
    }
    notify("Restored", "The System log shows it as a change of its own.");
  };

  // When, Who, Line and Change: shown or hidden and in order from the Columns menu, kept per
  // person; Go to line and Restore stay last.
  const cells: Record<string, (c: ChangeView) => ReactNode> = {
    when: (c) => <When change={c} today={today} />,
    who: (c) => (
      <span className="flex items-center gap-1">
        {c.personName}
        {c.edited || c.otherWriter ? (
          <Tooltip>
            <TooltipTrigger
              render={<TriangleAlertIcon className="size-3.5 text-orange-600" aria-label="Changed entry" />}
            />
            <TooltipContent>
              {c.edited
                ? "This entry was changed after it was written: don't trust it."
                : "Saved by another account than the one it names: don't trust it."}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </span>
    ),
    line: (c) => {
      const line = lineFor(c);
      const [customer, ...rest] = (c.lineText || "").split(" · ");
      return (
        <>
          <p className="flex flex-wrap items-center gap-x-1.5 font-semibold">
            {sourcesOf(c).map((x) => (
              <SourceBadge key={x} source={x} />
            ))}
            {(line && shownCustomerText(line)) || customer || "—"}
            {line?.divs.length ? (
              <span className="text-muted-foreground ml-1.5 text-xs font-normal">
                <DivisionText divs={line.divs} className="" />
              </span>
            ) : null}
          </p>
          <p className="text-muted-foreground text-xs">
            {rest.join(" · ") ||
              (c.entity === "list-value"
                ? "List value"
                : c.entity === DIVISION_ABBREVS.id
                  ? "Division abbreviation"
                  : c.entity === CUSTOMER_NAMES.id
                    ? "Customer shown name"
                    : c.entity === ROUTING_RULES.id
                      ? "Routing rule"
                      : c.entity === CUSTOMER_ASSIGNMENTS.id
                        ? "Customer assignment"
                        : c.entity === "pickup"
                          ? `Pickup ${c.recordId}`
                          : c.entity === "pre-route"
                            ? `Pre-route ${c.recordId}`
                            : "")}
          </p>
        </>
      );
    },
    change: (c) => changeText(c),
  };
  const logColumns = tableColumns.shown.map((col) => ({ ...col, cell: cells[col.id]! }));

  return (
    <div className="routing-page space-y-3">
      {rulesFirst.dialog}
      <AskAiBar
        mode="system-log"
        version={logViewKey(logView)}
        step={aiStep}
        onBegin={() => {
          turnBefore.current = logView;
        }}
        onEnd={() => {
          turnBefore.current = null;
        }}
        onUndo={() => {
          if (!aiStep) return;
          setLogView(aiStep.before);
          setAiSteps((steps) => steps.slice(0, -1));
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <SourcePicker
          value={source}
          counts={{
            A2000: ofMine.filter((c) => sourcesOf(c).includes("A2000")).length,
            Deposco: ofMine.filter((c) => sourcesOf(c).includes("Deposco")).length,
          }}
          onChange={setSource}
        />
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search the log — customer, PO, PT / SO, PICK UP #, person, field"
        />
        <MyCustomersChip
          hasMine={my.hasMine}
          mine={my.mine}
          count={my.count}
          labels={my.labels}
          note="Changes of other lines are hidden; those with no line (lists, pickups) stay."
          onChange={my.setMine}
        />
        {onLine ? (
          <span
            className="bg-muted inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
            data-testid="log-line-chip"
          >
            This line's changes, every date
            {changes.data?.changes[0] ? ` · ${changes.data.changes[0].lineText}` : ""}
            <button
              type="button"
              aria-label="Show every line's changes"
              className="hover:text-foreground"
              onClick={() => setOnLine("")}
            >
              <XIcon className="size-3" aria-hidden />
            </button>
          </span>
        ) : (
          <DayRangePicker label="Changed on" value={range} today={today} onChange={setRange} />
        )}
        {person ? (
          <FilterChip label={`Person: ${person}`} onClear={() => setPerson("")} testId="log-person-chip" />
        ) : null}
        {action ? (
          <FilterChip
            label={`Action: ${ACTION_WORDS[action]}`}
            onClear={() => setAction("")}
            testId="log-action-chip"
          />
        ) : null}
        <ColumnsMenu
          columns={tableColumns.ordered}
          order={tableColumns.order}
          hidden={tableColumns.hidden}
          onChange={tableColumns.setHidden}
          onReset={tableColumns.reset}
        />
        <p className="text-muted-foreground ml-auto text-xs">
          Every change people made, newest first · restore an earlier value · nothing is deleted
        </p>
      </div>
      <div className="flex gap-1 border-b">
        <span className="border-primary -mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium">
          By people
          <span className="bg-primary text-primary-foreground rounded px-1.5 text-xs tabular-nums">{shown.length}</span>
        </span>
      </div>
      <div className="overflow-x-auto rounded-md border" data-testid="system-log">
        <table className="w-full min-w-[900px] text-[13px]">
          <thead className="bg-locked text-left text-xs">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium">
              {logColumns.map((col) => (
                <th key={col.id} className={col.className}>
                  {col.header}
                </th>
              ))}
              <th className="w-28" />
              <th className="w-28" />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-muted-foreground p-6">
                  {source === "all"
                    ? "No change by people in these dates."
                    : `No ${source} change by people in these dates.`}
                </td>
              </tr>
            ) : null}
            {shown.map((c) => {
              const plan = planOf(c);
              return (
                <tr key={c.id} className="border-t align-top [&>td]:px-3 [&>td]:py-2">
                  {logColumns.map((col) => (
                    <td key={col.id} className={col.id === "change" ? "break-words" : undefined}>
                      {col.cell(c)}
                    </td>
                  ))}
                  <td>
                    {c.entity !== "list-value" &&
                    ((c.entity !== DIVISION_ABBREVS.id &&
                      c.entity !== CUSTOMER_NAMES.id &&
                      c.entity !== ROUTING_RULES.id &&
                      c.entity !== CUSTOMER_ASSIGNMENTS.id) ||
                      admin) ? (
                      <Button size="xs" variant="link" className="gap-1 px-0" onClick={() => goTo(c)}>
                        {c.entity === "pre-route"
                          ? "Go to pre-route"
                          : c.entity === DIVISION_ABBREVS.id ||
                              c.entity === CUSTOMER_NAMES.id ||
                              c.entity === ROUTING_RULES.id ||
                              c.entity === CUSTOMER_ASSIGNMENTS.id
                            ? "Go to Admin"
                            : "Go to line"}{" "}
                        <ArrowRightIcon className="size-3.5" />
                      </Button>
                    ) : null}
                  </td>
                  <td>
                    {plan.ok ? (
                      <Button size="xs" variant="outline" className="gap-1" onClick={() => void restore(c)}>
                        <RotateCcwIcon className="size-3.5" />
                        Restore
                      </Button>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger render={<span className="text-muted-foreground cursor-help text-xs" />}>
                          {plan.short}
                        </TooltipTrigger>
                        <TooltipContent>{plan.why}</TooltipContent>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <footer className="text-muted-foreground flex flex-wrap gap-x-4 text-xs">
        <span>
          {countText(shown.length, "change")} · Changed on{" "}
          {range.from ? `${formatDay(range.from)} – ${formatDay(range.to ?? today)}` : "any date"}
        </span>
        <span className="ml-auto">By people only · A2000 and Deposco changes come later</span>
      </footer>
      {flow.dialogs}
    </div>
  );
}

/** A filter Ask AI set that the toolbar has no control for: its name, and × to take it off. */
function FilterChip({ label, onClear, testId }: { label: string; onClear: () => void; testId: string }) {
  return (
    <span
      className="bg-muted inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
      data-testid={testId}
    >
      {label}
      <button type="button" aria-label={`Remove ${label}`} className="hover:text-foreground" onClick={onClear}>
        <XIcon className="size-3" aria-hidden />
      </button>
    </span>
  );
}
